import type { SelectBehavior } from '../OptionSelectBase/multiSelection'

type SelectableOption = { value: string; onSelectBehavior?: string }

const hasBehavior = (option: SelectableOption, behavior: SelectBehavior) =>
  (option.onSelectBehavior ?? 'none') === behavior

const isRegular = (option: SelectableOption) =>
  !hasBehavior(option, 'select_all') && !hasBehavior(option, 'deselect_all')

/**
 * The multi-select selection after the visitor taps `clicked`.
 *
 * A "None" option (`deselect_all`) clears every other option, and picking any other
 * option clears it. A "Select All" option (`select_all`) selects every regular option,
 * and unpicking any of them clears it. A second tap on either toggles only itself off.
 *
 * @param options every option in the list, in author order
 * @param selection the option values currently selected
 * @param clicked the option tapped
 * @returns the option values to store
 */
export function nextSelectedValues(
  options: readonly SelectableOption[],
  selection: readonly string[],
  clicked: SelectableOption,
): string[] {
  const isSelected = selection.includes(clicked.value)
  const withoutClicked = selection.filter((v) => v !== clicked.value)

  if (hasBehavior(clicked, 'deselect_all')) {
    return isSelected ? withoutClicked : [clicked.value]
  }

  const regularValues = options.filter(isRegular).map((o) => o.value)

  if (hasBehavior(clicked, 'select_all')) {
    return isSelected ? withoutClicked : [...regularValues, clicked.value]
  }

  const toggled = isSelected ? withoutClicked : [...selection, clicked.value]
  const everyRegularSelected = regularValues.every((v) => toggled.includes(v))
  const cleared = new Set(
    options
      .filter(
        (o) =>
          hasBehavior(o, 'deselect_all') || (!everyRegularSelected && hasBehavior(o, 'select_all')),
      )
      .map((o) => o.value),
  )
  return toggled.filter((v) => !cleared.has(v))
}
